/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import { ArrowRight, ArrowRightLeft, CalendarClock, Rocket } from "lucide-react";

import { getTransferItemTitle, ActionItem } from "@src/@types/MainItem";
import { ProviderBadge } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import IconTile from "@src/components/ui/IconTile";
import {
  Cell,
  FlexCell,
  ListRow,
  Num,
  Pill,
  Primary,
  Secondary,
  Stack,
  StatePill,
  T9,
  formatDateTime,
  formatShortDateTime,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

/** The Transfers table. */
export const TRANSFER_COLUMNS: ListColumn[] = [
  { key: "name", label: "Name", width: "minmax(240px, 2.4fr)" },
  { key: "status", label: "Status", width: "112px" },
  { key: "route", label: "Source → Target", width: "minmax(180px, 1.3fr)" },
  { key: "instances", label: "Instances", width: "76px", align: "right" },
  { key: "activity", label: "Last sync / Next run", width: "150px" },
  { key: "owner", label: "Owner", width: "96px" },
  { key: "updated", label: "Updated", width: "100px" },
];

/** The Deployments table. */
export const DEPLOYMENT_COLUMNS: ListColumn[] = [
  { key: "name", label: "Name", width: "minmax(240px, 2.4fr)" },
  { key: "status", label: "Status", width: "112px" },
  { key: "route", label: "Source → Target", width: "minmax(180px, 1.3fr)" },
  { key: "instances", label: "Instances", width: "76px", align: "right" },
  { key: "created", label: "Created", width: "104px" },
  { key: "owner", label: "Owner", width: "100px" },
  { key: "updated", label: "Updated", width: "104px" },
];

const Arrow = styled(ArrowRight)`
  color: ${T9.slate400};
  flex-shrink: 0;
`;
const Route = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 20px;
`;
const Schedule = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 16px;
  color: ${T9.brand600};
  white-space: nowrap;
`;
const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  & > span {
    flex-shrink: 0;
  }
`;

/** "replica" transfers are incremental replicas; "live_migration" ones are migrations. */
export const transferScenario = (item: ActionItem) => {
  if (item.type === "deployment") {
    return item.transfer_scenario_type;
  }
  return item.scenario;
};
export const scenarioLabel = (scenario?: string | null) =>
  scenario === "live_migration" ? "Migration" : "Replica";

type Props = {
  item: ActionItem;
  onClick: () => void;
  selected: boolean;
  selectable?: boolean;
  showScheduleIcon?: boolean;
  endpointType: (endpointId: string) => string;
  getEndpointName?: (endpointId: string) => string | undefined;
  getUserName: (userId: string) => string | undefined;
  userNameLoading: boolean;
  onSelectedChange: (value: boolean) => void;
  actions?: DropdownAction[];
};
@observer
class TransferListItem extends React.Component<Props> {
  renderName() {
    const { item } = this.props;
    const title = getTransferItemTitle(item) || "";
    const scenario = transferScenario(item);
    const isDeployment = item.type === "deployment";
    const subtitle =
      item.notes && item.instances.length
        ? item.instances.join(", ")
        : item.description || "";
    return (
      <FlexCell gap={12}>
        <IconTile
          icon={isDeployment ? Rocket : ArrowRightLeft}
          size={28}
          tone={isDeployment ? "brand" : "info"}
        />
        <Stack>
          <NameLine>
            <Primary title={title}>{title}</Primary>
            <Pill tone={scenario === "live_migration" ? "brand" : "info"}>
              {scenarioLabel(scenario)}
            </Pill>
          </NameLine>
          {subtitle ? <Secondary title={subtitle}>{subtitle}</Secondary> : null}
        </Stack>
      </FlexCell>
    );
  }

  renderRoute() {
    const { item } = this.props;
    const sourceType = this.props.endpointType(item.origin_endpoint_id);
    const targetType = this.props.endpointType(item.destination_endpoint_id);
    const sourceName =
      this.props.getEndpointName?.(item.origin_endpoint_id) || sourceType;
    const targetName =
      this.props.getEndpointName?.(item.destination_endpoint_id) || targetType;
    return (
      <Stack title={`${sourceName} → ${targetName}`}>
        <Route>
          <ProviderBadge provider={sourceType} title={sourceName} />
          <Arrow size={14} />
          <ProviderBadge provider={targetType} title={targetName} />
        </Route>
        <Secondary>
          {sourceName} → {targetName}
        </Secondary>
      </Stack>
    );
  }

  renderActivity() {
    const { item } = this.props;
    const ran = Boolean(
      item.last_execution_status && item.last_execution_status !== "UNEXECUTED",
    );
    return (
      <Stack>
        <Cell title={formatDateTime(item.updated_at || item.created_at)}>
          {ran ? timeAgo(item.updated_at || item.created_at) : "Never synced"}
        </Cell>
        {this.props.showScheduleIcon ? (
          <Schedule title="Scheduling is enabled: the transfer runs automatically">
            <CalendarClock size={12} />
            Scheduled
          </Schedule>
        ) : null}
      </Stack>
    );
  }

  render() {
    const { item } = this.props;
    const isDeployment = item.type === "deployment";
    const owner = this.props.userNameLoading
      ? "Loading…"
      : this.props.getUserName(item.user_id) || item.user_id;
    return (
      <ListRow
        columns={isDeployment ? DEPLOYMENT_COLUMNS : TRANSFER_COLUMNS}
        selectable={this.props.selectable}
        selected={this.props.selected}
        onSelectedChange={this.props.onSelectedChange}
        onClick={this.props.onClick}
        actions={this.props.actions}
        testId="transferListItem"
      >
        {this.renderName()}
        <div>
          <StatePill status={item.last_execution_status} />
        </div>
        {this.renderRoute()}
        <Num>{item.instances.length}</Num>
        {isDeployment ? (
          <Cell title={formatDateTime(item.created_at)}>
            {formatShortDateTime(item.created_at)}
          </Cell>
        ) : (
          this.renderActivity()
        )}
        <Cell title={owner}>{owner}</Cell>
        <Cell title={formatDateTime(item.updated_at)}>
          {item.updated_at ? formatShortDateTime(item.updated_at) : "—"}
        </Cell>
      </ListRow>
    );
  }
}

export default TransferListItem;
